/* © Facilogi. See COPYRIGHT file for full copyright & licensing details. */

import Feathers from "@feathersjs/feathers";

import { CustomError } from "@Internal/Models";
import { Autobind } from "@Internal/Utils";

import {
    AgentKbConversation, AgentKbExchange, AgentKbFilters, AgentKbPage, AgentKbThread,
    AgentKbUsage
} from "App/Models/AgentKb";

import { ProcessException } from "./Base";

type Result<T> = Promise<[ T | undefined, CustomError | undefined ]>;

/* Lecture des echanges de l'agent IA. Le back relaie le service de l'agent sans
   le serialiser ( exemption `Internal/AgentKb` de `Web/Hooks.ts` ) : on lit donc
   la reponse de Feathers telle quelle, comme pour le tableau de bord des revenus. */
export default class AgentKbService {
    private exchanges: Feathers.Service<any>;
    private conversations: Feathers.Service<any>;
    private usage: Feathers.Service<any>;

    constructor(
        exchanges: Feathers.Service<any>,
        conversations: Feathers.Service<any>,
        usage: Feathers.Service<any>
    ) {
        this.exchanges = exchanges;
        this.conversations = conversations;
        this.usage = usage;
    }

    @Autobind
    async Exchanges( filters: AgentKbFilters ): Result<AgentKbPage<AgentKbExchange>> {
        return this.list( this.exchanges, filters );
    }

    @Autobind
    async Conversations( filters: AgentKbFilters ): Result<AgentKbPage<AgentKbConversation>> {
        return this.list( this.conversations, filters );
    }

    /* Un fil entier, par son identifiant — `get` du relais. */
    @Autobind
    async Conversation( id: string ): Result<AgentKbThread> {
        try {
            const result = await this.conversations.get( id );

            if( result === undefined || result.echanges === undefined )
                return [ undefined, this.error( result, "AgentKb.Conversation" ) ];

            return [ result as AgentKbThread, undefined ];
        } catch( error ) {
            return [ undefined, await ProcessException( error ) ];
        }
    }

    @Autobind
    async Usage( by: string, filters: AgentKbFilters ): Result<AgentKbUsage> {
        try {
            const result = await this.usage.find( { query: { ...this.query( filters ), by } } );

            if( result === undefined || result.items === undefined )
                return [ undefined, this.error( result, "AgentKb.Usage" ) ];

            return [ result as AgentKbUsage, undefined ];
        } catch( error ) {
            return [ undefined, await ProcessException( error ) ];
        }
    }

    private async list<T>(
        service: Feathers.Service<any>,
        filters: AgentKbFilters
    ): Result<AgentKbPage<T>> {
        try {
            const result = await service.find( { query: this.query( filters ) } );

            if( result === undefined || result.items === undefined )
                return [ undefined, this.error( result, "AgentKb.List" ) ];

            return [ result as AgentKbPage<T>, undefined ];
        } catch( error ) {
            return [ undefined, await ProcessException( error ) ];
        }
    }

    /* Seuls les filtres renseignes partent : un champ vide n'est pas un filtre. */
    private query( filters: AgentKbFilters ): { [ key: string ]: string } {
        const query: { [ key: string ]: string } = {};

        Object.keys( filters ).forEach( ( key: string ): void => {
            const value = ( filters as { [ key: string ]: unknown } )[ key ];

            if( value !== undefined && value !== null && value !== "" )
                query[ key ] = String( value );
        } );

        return query;
    }

    private error( result: any, fallbackId: string ): CustomError {
        const id = ( result !== undefined && result.Id !== undefined ) ? result.Id : fallbackId;
        const message = ( result !== undefined && result.Message !== undefined )
            ? result.Message
            : "Unexpected agent response";

        return new CustomError( id, message );
    }
}
